/* eslint-disable react/prop-types */
import styled from '@emotion/styled';
import classNames from 'classnames';
import Icon from 'components/icon';
import React, { useEffect, useRef, useState } from 'react';
import { ListGroup } from 'react-bootstrap';
import { Form } from 'react-bootstrap';

const CustomInput = styled(Form.Control)`
  padding: 5px 8px;
  &:focus {
    box-shadow: none !important;
  }
`;

const CustomItem = styled(ListGroup.Item)`
  border: 0 !important;
  border-radius: 0.375rem;
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: all;
  }
  &:not(:last-child) {
    margin-bottom: 0.25rem;
  }
`;

const checkDescription = (desc, searchText) => {
  if (desc) {
    return desc.toLowerCase().includes(searchText);
  } else {
    return false;
  }
};

const filterNestedObjects = (arr, searchText) => {
  return arr
    .map(item => {
      if (item.group) {
        if (
          item.label.toLowerCase().includes(searchText) ||
          checkDescription(item?.descText, searchText)
        ) {
          return item;
        } else {
          const filteredGroup = filterNestedObjects(item.group, searchText);
          if (filteredGroup.length > 0) {
            return { ...item, group: filteredGroup };
          } else {
            return null;
          }
        }
      } else if (
        item.label.toLowerCase().includes(searchText) ||
        checkDescription(item?.descText, searchText)
      ) {
        return item;
      }
      return null;
    })
    .filter(Boolean);
};

const SingleItem = ({
  item,
  handleSelectedOption,
  selectedOption,
  searchInput
}) => {
  if (selectedOption !== item['value'] || searchInput.length > 0) {
    let isActive = selectedOption === item['value'] && searchInput.length > 0;
    return (
      <CustomItem
        action
        disabled={item.disabled}
        onClick={() => handleSelectedOption(item)}
        active={isActive}
      >
        <p className="m-0 p-0">{item.label}</p>
        {item.desc ? (
          <div className="">
            <small className="text-muted fs-9">{item.desc}</small>
          </div>
        ) : null}
      </CustomItem>
    );
  } else {
    return null;
  }
};

const GroupItem = ({
  item,
  handleSelectedOption,
  selectedOption,
  searchInput
}) => {
  return (
    <CustomItem>
      <span className="fw-bold">{item.label}</span>
      <ListGroup>
        {item.group?.map((member, i) => (
          <SingleItem
            searchInput={searchInput}
            key={`GroupItem-${i}-${item['value']}`}
            item={member}
            selectedOption={selectedOption}
            handleSelectedOption={handleSelectedOption}
          />
        ))}
      </ListGroup>
    </CustomItem>
  );
};

const Selector = ({
  dataSet,
  inputProps,
  footerTextBtn,
  close,
  className,
  placeholder = 'Select Option',
  onChange = () => {},
  defaultSelect = '',
  size = 'sm',
  allowFooter = false,
  startIcon,
  startIconProps,
  formikError
}) => {
  const [filterData, setFilterData] = useState([]);
  const [selectedOption, setSelectedOption] = useState('');
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [selectedData, setSelectedData] = useState({});
  const mainRef = useRef();
  const inputRef = useRef();
  const ref = useRef(null);
  const customItemRef = useRef();

  useEffect(() => {
    if (dataSet?.length > 0) {
      setSelectedOption(defaultSelect);
    }
  }, [defaultSelect, dataSet]);

  useEffect(() => {
    // let filteredData = data.filter(data =>
    //   data['label'].toLowerCase().includes(searchInput.toLowerCase())
    // );
    let copyData = [...dataSet];

    const filteredData = filterNestedObjects(
      copyData,
      searchInput.toLowerCase()
    );
    setFilterData(filteredData);
  }, [searchInput, dataSet]);

  useEffect(() => {
    if (close) {
      setOpen(false);
    }
  }, [close]);

  useEffect(() => {
    let findItem = dataSet.find(item => {
      if (item.group) {
        let groupItem = item.group.find(mem => mem['value'] === selectedOption);
        if (groupItem) {
          setSelectedData(groupItem);
        }
        return false;
      } else {
        return item['value'] === selectedOption;
      }
    });
    if (findItem) {
      setSelectedData(findItem);
    }
  }, [selectedOption, dataSet]);

  const setFocus = () => {
    inputRef.current.focus();
  };

  const handleSelectedOption = item => {
    setSelectedOption(item.value);
    onChange(item.value, item);
    setSearchInput('');
    setOpen(false);
  };

  const handleClickOutside = event => {
    if (
      ref.current &&
      !ref.current.contains(event.target) &&
      mainRef.current &&
      !mainRef.current.contains(event.target) &&
      !event.target.closest('.modal') &&
      !event.target.closest('.modal-backdrop')
    ) {
      setOpen(false);
      setSearchInput('');
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setFocus();
      if (customItemRef.current) {
        customItemRef.current.scrollTop = 0;
      }
    }
  }, [open]);

  const handleClickOpen = () => {
    setOpen(!open);
  };

  const handleSearch = event => {
    setSearchInput(typeof event === 'string' ? event : event.target.value);
  };

  return (
    <div className="w-100 custom-selector position-relative">
      <div
        className={classNames('no-select position-absolute cursor-pointer')}
        style={{
          left: '8px',
          top: '16px',
          transform: 'translateY(-55%)'
        }}
      >
        <Icon fontSize="fs-10" {...startIconProps} icon={startIcon} />
      </div>

      <div
        className={classNames(
          'no-select text-400 position-absolute cursor-pointer',
          { 'pe-none': inputProps?.disabled }
        )}
        style={{
          right: '10px',
          top: '19px',
          transform: 'translateY(-55%)'
        }}
        onClick={handleClickOpen}
      >
        <Icon fontSize="fs-10" icon="fa6-solid:angle-down" />
      </div>
      <Form.Control
        size={size !== 'md' ? size : ''}
        ref={mainRef}
        readOnly
        onClick={handleClickOpen}
        className={classNames('cursor-pointer', className, {
          'pe-none': inputProps?.readOnly,
          'ps-5': startIcon
        })}
        type="text"
        value={selectedOption ? selectedData['label'] : ''}
        placeholder={placeholder}
        {...inputProps}
      />
      {formikError}
      <div
        ref={ref}
        className={`dropdown-menu p-0 ${open ? 'show' : ''}`}
        data-popper-reference-hidden="false"
        data-popper-escaped="false"
        data-popper-placement="bottom-start"
        style={{
          position: 'absolute',
          width: '100%',
          top: '40px',
          left: '0px',
          transition: 'all 0.5s ease'
        }}
      >
        <div className="" style={{ padding: '0.25rem' }}>
          <CustomInput
            onChange={handleSearch}
            value={searchInput}
            ref={inputRef}
            type="text"
            autoComplete="new-password"
            inputMode="none"
            autoCorrect="off"
            spellCheck="false"
            placeholder="Search"
          />
          <div className="pt-1  ">
            <ListGroup
              ref={customItemRef}
              className="custom-scrollbar"
              style={{
                maxHeight: '250px',
                overflowY: 'auto',
                overflowX: 'hidden'
              }}
            >
              {selectedData.label &&
              searchInput.length <= 0 &&
              selectedOption ? (
                <CustomItem
                  action
                  id={`item-${selectedData.value}`}
                  onClick={() => handleSelectedOption(selectedData)}
                  active={selectedOption == selectedData['value']}
                  //   ref={selectedOption == item['value'] ? customItemRef : null}
                >
                  {/* {selectedData.label} */}
                  <p className="m-0 p-0">{selectedData.label}</p>
                  {selectedData.desc ? (
                    <div className="">
                      <small className="text-200 dark__text-200  fs-9">
                        {selectedData.desc}
                      </small>
                    </div>
                  ) : null}
                </CustomItem>
              ) : null}

              <></>
              {filterData?.map((item, i) => (
                <React.Fragment key={`${i}-selector-options`}>
                  {item.group ? (
                    <GroupItem
                      handleSelectedOption={handleSelectedOption}
                      selectedOption={selectedOption}
                      item={item}
                      searchInput={searchInput}
                      key={`listItem-${i}-${item['value']}`}
                      //   active={selectedOption == item['value']}
                      //   ref={selectedOption == item['value'] ? customItemRef : null}
                    />
                  ) : (
                    <SingleItem
                      handleSelectedOption={handleSelectedOption}
                      selectedOption={selectedOption}
                      item={item}
                      searchInput={searchInput}
                      key={`listItem-${i}-${item['value']}`}
                      //   active={selectedOption == item['value']}
                      //   ref={selectedOption == item['value'] ? customItemRef : null}
                    />
                  )}
                </React.Fragment>
              ))}

              {filterData.length === 0 ? (
                <CustomItem disabled>No Results Found</CustomItem>
              ) : null}
            </ListGroup>
          </div>
        </div>
        {allowFooter ? (
          <div className="border-top">
            {footerTextBtn(
              filterData.length === 0 ? searchInput : '',
              handleClickOpen,
              handleSearch
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Selector;
